import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Quote } from 'lucide-react';

/**
 * Brand philosophy quote — the company's "soul" statement.
 * Multi-section narrative:
 *   1. Etymology (highlighted Greek keywords)
 *   2. Body paragraphs (mission, services, values)
 *   3. Emphatic closing line (gold accent)
 *
 * Museum-grade typography with Greek-meander corners, gold flourishes,
 * floating embers and shimmer accents.
 */

const MeanderCorner = ({ className = '', flipX = false, flipY = false }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 80 80"
    className={className}
    style={{
      transform: `scaleX(${flipX ? -1 : 1}) scaleY(${flipY ? -1 : 1})`,
    }}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <path d="M2 78 L2 50 L18 50 L18 64 L8 64 L8 56 L24 56 L24 70 L14 70 L14 78" />
    <path d="M30 78 L30 60 L46 60 L46 74 L36 74 L36 66 L52 66 L52 78" />
    <path d="M58 78 L58 70 L74 70 L74 78" />
  </svg>
);

const SERIF_STACK = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";

const PhilosophySection = ({ labels = {} }) => {
  const reduceMotion = useReducedMotion();
  const eyebrow = labels.eyebrow || 'Η ΦΙΛΟΣΟΦΙΑ ΜΑΣ';
  const etymologyText = labels.etymology ||
    'Η λέξη «ασθενής» προέρχεται από το α- στερητικό και το σθένος, δηλώνοντας εκείνον που έχει ανάγκη υποστήριξης, φροντίδας και ενδυνάμωσης.';
  const paragraphsRaw = labels.paragraphs;
  const paragraphs = Array.isArray(paragraphsRaw)
    ? paragraphsRaw
    : (typeof paragraphsRaw === 'string' && paragraphsRaw)
      ? [paragraphsRaw]
      : [
    'Πιστεύουμε ότι κάθε επικοινωνία με έναν επαγγελματία υγείας είναι μια ευκαιρία να προσφέρουμε ξανά λίγη από αυτή τη δύναμη. Γι\u2019 αυτό δίνουμε ιδιαίτερη σημασία στην πρώτη κλήση, όπου η ευγένεια, η κατανόηση και ο επαγγελματισμός μπορούν να κάνουν τη διαφορά.',
    'Στην J.House of Secretary δεν διαχειριζόμαστε απλώς κλήσεις και ραντεβού. Υποστηρίζουμε ανθρώπους σε στιγμές που αναζητούν καθοδήγηση, φροντίδα και πρόσβαση στις κατάλληλες υπηρεσίες υγείας.',
    'Μέσα από τις υπηρεσίες γραμματειακής υποστήριξης, τη Γραμμή Ασθενή και τη Διεθνή Πρόσβαση Υγείας, φροντίζουμε ώστε κάθε άνθρωπος να αισθάνεται ότι ακούγεται, κατανοείται και καθοδηγείται με σεβασμό και υπευθυνότητα.',
    'Από την πρώτη τηλεφωνική επικοινωνία έως τη σύνδεση με εξειδικευμένους επαγγελματίες υγείας στην Κύπρο και το εξωτερικό, αποστολή μας είναι να μετατρέπουμε την αβεβαιότητα σε σαφή καθοδήγηση, τα εμπόδια σε λύσεις και το άγχος σε αίσθημα σιγουριάς.',
  ];
  const closing = labels.closing ||
    'Γιατί πίσω από κάθε κλήση δεν βρίσκεται απλώς ένας ασθενής, αλλά ένας άνθρωπος που αξίζει να νιώθει ότι δεν είναι μόνος στη διαδρομή προς την υγεία.';

  // Highlight Greek-etymology keywords in gold within the etymology line.
  const renderEtymology = (text) => {
    const KEYWORDS = ['ασθενής', 'α-', 'σθένος'];
    const parts = [];
    let remaining = text;
    let keyIdx = 0;
    while (remaining.length > 0) {
      let nextIdx = -1;
      let nextKeyword = null;
      for (const kw of KEYWORDS) {
        const idx = remaining.indexOf(kw);
        if (idx !== -1 && (nextIdx === -1 || idx < nextIdx)) {
          nextIdx = idx;
          nextKeyword = kw;
        }
      }
      if (nextIdx === -1) {
        parts.push(<React.Fragment key={`t${keyIdx++}`}>{remaining}</React.Fragment>);
        break;
      }
      if (nextIdx > 0) {
        parts.push(<React.Fragment key={`t${keyIdx++}`}>{remaining.slice(0, nextIdx)}</React.Fragment>);
      }
      parts.push(
        <em
          key={`k${keyIdx++}`}
          className="not-italic font-semibold text-[#e4c275]"
          style={{ textShadow: '0 0 18px rgba(201,168,76,0.35)' }}
        >
          {nextKeyword}
        </em>
      );
      remaining = remaining.slice(nextIdx + nextKeyword.length);
    }
    return parts;
  };

  // Floating ember particles
  const [embers] = useState(() =>
    Array.from({ length: 16 }, () => ({
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 11 + Math.random() * 9,
      size: 1 + Math.random() * 2.2,
      opacity: 0.15 + Math.random() * 0.35,
    }))
  );

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.1 }}
      className="relative bg-[#0a1628] py-12 md:py-20 overflow-hidden"
      data-testid="philosophy-section"
    >
      {/* Background: subtle radial gold halo */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 75% 70% at 50% 45%, rgba(201,168,76,0.07) 0%, transparent 65%)',
        }}
      />

      {/* Rising ember particles */}
      {!reduceMotion && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
          {embers.map((e, i) => (
            <motion.span
              key={i}
              className="absolute bottom-0 rounded-full bg-[#e4c275]"
              style={{
                left: `${e.left}%`,
                width: e.size,
                height: e.size,
                opacity: e.opacity,
                boxShadow: '0 0 8px rgba(201,168,76,0.5)',
              }}
              animate={{ y: ['0%', '-1400%'], opacity: [0, e.opacity, 0] }}
              transition={{
                duration: e.duration,
                delay: e.delay,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          ))}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 relative">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center justify-center gap-3 md:gap-4 mb-8 md:mb-12"
        >
          <motion.span
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent via-[#c9a84c]/60 to-[#c9a84c]/80 origin-right"
          />
          <span aria-hidden="true" className="text-[#c9a84c] text-base md:text-lg">✦</span>
          <span className="text-[10px] md:text-xs uppercase tracking-[0.5em] text-[#c9a84c] font-semibold whitespace-nowrap">
            {eyebrow}
          </span>
          <span aria-hidden="true" className="text-[#c9a84c] text-base md:text-lg">✦</span>
          <motion.span
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="h-px w-16 md:w-24 bg-gradient-to-l from-transparent via-[#c9a84c]/60 to-[#c9a84c]/80 origin-left"
          />
        </motion.div>

        {/* Quote frame with meander corners */}
        <div className="relative">
          {/* 4 Greek meander corners */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="absolute -top-2 -left-2 md:-top-3 md:-left-3 text-[#c9a84c]/30 w-12 h-12 md:w-16 md:h-16"
          >
            <MeanderCorner className="w-full h-full" flipY />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="absolute -top-2 -right-2 md:-top-3 md:-right-3 text-[#c9a84c]/30 w-12 h-12 md:w-16 md:h-16"
          >
            <MeanderCorner className="w-full h-full" flipX flipY />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="absolute -bottom-2 -left-2 md:-bottom-3 md:-left-3 text-[#c9a84c]/30 w-12 h-12 md:w-16 md:h-16"
          >
            <MeanderCorner className="w-full h-full" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="absolute -bottom-2 -right-2 md:-bottom-3 md:-right-3 text-[#c9a84c]/30 w-12 h-12 md:w-16 md:h-16"
          >
            <MeanderCorner className="w-full h-full" flipX />
          </motion.div>

          {/* Subtle giant background quote marks */}
          <Quote
            aria-hidden="true"
            className="absolute top-3 left-3 md:top-2 md:left-6 w-12 h-12 md:w-20 md:h-20 text-[#c9a84c]/12"
            fill="currentColor"
          />
          <Quote
            aria-hidden="true"
            className="absolute bottom-3 right-3 md:bottom-2 md:right-6 w-12 h-12 md:w-20 md:h-20 text-[#c9a84c]/12 rotate-180"
            fill="currentColor"
          />

          {/* Body */}
          <blockquote className="relative px-6 md:px-20 py-10 md:py-14 text-center">
            {/* 1. Etymology — leading line with gold-highlighted keywords */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="text-lg md:text-2xl lg:text-3xl leading-relaxed md:leading-[1.55] text-slate-50 italic"
              style={{ fontFamily: SERIF_STACK }}
            >
              {renderEtymology(etymologyText)}
            </motion.p>

            {/* Mini divider after etymology */}
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              whileInView={{ opacity: 1, width: '5rem' }}
              viewport={{ once: true }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="mx-auto my-6 md:my-9 h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent"
            />

            {/* 2. Body paragraphs */}
            <div className="space-y-5 md:space-y-7">
              {paragraphs.map((p, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.0 + i * 0.15, duration: 0.7 }}
                  className="text-base md:text-lg lg:text-xl leading-relaxed md:leading-[1.75] text-slate-200/90"
                  style={{ fontFamily: SERIF_STACK }}
                >
                  {p}
                </motion.p>
              ))}
            </div>

            {/* Closing flourish: tiny gold diamond divider */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.7, duration: 0.5 }}
              className="flex items-center justify-center gap-2 my-7 md:my-10"
              aria-hidden="true"
            >
              <span className="h-px w-12 md:w-20 bg-gradient-to-r from-transparent to-[#c9a84c]/70" />
              <span className="text-[#c9a84c] text-xs">◆</span>
              <span className="h-px w-12 md:w-20 bg-gradient-to-l from-transparent to-[#c9a84c]/70" />
            </motion.div>

            {/* 3. Emphatic closing line */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 1.85, duration: 0.8 }}
              className="text-base md:text-xl lg:text-2xl leading-relaxed md:leading-[1.6] text-[#e4c275] italic font-medium"
              style={{
                fontFamily: SERIF_STACK,
                textShadow: '0 0 22px rgba(201,168,76,0.25)',
              }}
            >
              {closing}
            </motion.p>
          </blockquote>
        </div>

        {/* Shimmer line at bottom */}
        <div className="mt-10 md:mt-12 mx-auto relative h-px w-48 md:w-72 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent)',
            }}
          />
          {!reduceMotion && (
            <motion.div
              className="absolute inset-y-0 w-1/3"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(255,225,150,0.95), transparent)',
              }}
              initial={{ x: '-100%' }}
              whileInView={{ x: '350%' }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 4, ease: 'easeInOut' }}
            />
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default PhilosophySection;
